'use client';
import { useEffect, useState } from 'react';

interface Props {
  isDataReady: boolean;
  onComplete:  () => void;
}

const FEATURES = [
  'Live NOAA GFS Wind Data',
  '45,000 Animated Wind Particles',
  '8K Planet Surface Textures',
  'Real-time Global Atmosphere',
];

const MIN_DURATION = 13000; // ms

export default function SplashScreen({ isDataReady, onComplete }: Props) {
  const [progress,    setProgress]    = useState(0);
  const [timerDone,   setTimerDone]   = useState(false);
  const [statusIdx,   setStatusIdx]   = useState(0);
  const [visible,     setVisible]     = useState(true);
  const [featuresIn,  setFeaturesIn]  = useState([false,false,false,false]);

  const STATUS = [
    'Initialising atmosphere…',
    'Fetching live wind data…',
    'Mapping global particles…',
    'Entering atmosphere…',
  ];

  // Animate features in one by one
  useEffect(() => {
    FEATURES.forEach((_, i) => {
      setTimeout(() => {
        setFeaturesIn(prev => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 1200 + i * 600);
    });
  }, []);

  // Cycle status text
  useEffect(() => {
    const id = setInterval(() => {
      setStatusIdx(i => (i < STATUS.length - 1 ? i + 1 : i));
    }, MIN_DURATION / STATUS.length);
    return () => clearInterval(id);
  }, []);

  // Progress bar — smooth fill over MIN_DURATION
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const t       = Math.min(elapsed / MIN_DURATION, 1);
      // Ease: fast start, slow finish
      const eased   = t < 0.7
        ? t / 0.7 * 0.85
        : 0.85 + (t - 0.7) / 0.3 * 0.10;
      setProgress(Math.round(eased * 100));
      if (t >= 1) {
        clearInterval(id);
        setTimerDone(true);
      }
    }, 50);
    return () => clearInterval(id);
  }, []);

  // Dismiss when both timer and data are ready
  useEffect(() => {
    if (timerDone && isDataReady) {
      setProgress(100);
      setTimeout(() => setVisible(false), 400);
      setTimeout(() => onComplete(), 900);
    }
  }, [timerDone, isDataReady]);

  return (
    <div
      style={{
        position:        'fixed',
        inset:           0,
        background:      '#00060e',
        zIndex:          9999,
        display:         'flex',
        flexDirection:   'column',
        alignItems:      'center',
        justifyContent:  'center',
        fontFamily:      "'Inter', system-ui, sans-serif",
        WebkitFontSmoothing: 'antialiased',
        opacity:         visible ? 1 : 0,
        transition:      'opacity 0.5s ease',
        padding:         '32px',
      }}
    >
      {/* Stars */}
      <div style={{
        position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none',
      }}>
        {Array.from({ length: 80 }).map((_, i) => (
          <div key={i} style={{
            position:        'absolute',
            width:           (((i * 7 + 3) % 20) / 10 + 1) + 'px',
            height:          (((i * 7 + 3) % 20) / 10 + 1) + 'px',
            borderRadius:    '50%',
            background:      'rgba(255,255,255,' + (((i * 13 + 5) % 60) / 100 + 0.2) + ')',
            left:            ((i * 37 + 11) % 100) + '%',
            top:             ((i * 53 + 7) % 100) + '%',
          }}/>
        ))}
      </div>

      {/* Logo */}
      <div style={{
        marginBottom: '28px',
        animation:    'splashLogoIn 0.8s cubic-bezier(.16,1,.3,1) both',
      }}>
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
          <circle cx="40" cy="46" r="24"
            stroke="rgba(255,255,255,0.88)" strokeWidth="2.5"/>
          <ellipse cx="40" cy="46" rx="24" ry="6"
            stroke="rgba(255,255,255,0.16)" strokeWidth="1"/>
          <path d="M 11 32 A 32 32 0 0 1 69 32"
            stroke="#00e5cc" strokeWidth="2.8" strokeLinecap="round" fill="none"/>
        </svg>
      </div>

      {/* Wordmark */}
      <div style={{
        display:       'flex',
        alignItems:    'baseline',
        marginBottom:  '8px',
        animation:     'splashFadeUp 0.8s 0.2s both',
      }}>
        <span style={{
          fontSize:      '36px',
          fontWeight:    300,
          letterSpacing: '7px',
          color:         '#ffffff',
        }}>
          ATMOSPHERE
        </span>
        <span style={{
          fontSize:      '36px',
          fontWeight:    700,
          letterSpacing: '2px',
          color:         '#00e5cc',
        }}>
          8K
        </span>
      </div>

      {/* Mission */}
      <div style={{
        fontSize:      '12px',
        fontWeight:    400,
        letterSpacing: '4px',
        color:         'rgba(255,255,255,0.3)',
        textTransform: 'uppercase',
        marginBottom:  '48px',
        animation:     'splashFadeUp 0.8s 0.35s both',
      }}>
        The Atmosphere, in 8K
      </div>

      {/* Features */}
      <div style={{
        display:       'flex',
        flexDirection: 'column',
        gap:           '10px',
        marginBottom:  '56px',
        minWidth:      '260px',
      }}>
        {FEATURES.map((f, i) => (
          <div key={f} style={{
            display:    'flex',
            alignItems: 'center',
            gap:        '10px',
            opacity:    featuresIn[i] ? 1 : 0,
            transform:  featuresIn[i] ? 'translateX(0)' : 'translateX(-20px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}>
            <div style={{
              width:        '6px',
              height:       '6px',
              borderRadius: '50%',
              background:   '#00e5cc',
              flexShrink:   0,
            }}/>
            <span style={{
              fontSize:      '13px',
              color:         'rgba(255,255,255,0.65)',
              letterSpacing: '0.02em',
            }}>
              {f}
            </span>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div style={{
        width:        '240px',
        marginBottom: '14px',
        animation:    'splashFadeUp 0.8s 0.5s both',
      }}>
        <div style={{
          height:       '2px',
          background:   'rgba(255,255,255,0.08)',
          borderRadius: '1px',
          overflow:     'hidden',
        }}>
          <div style={{
            height:           '100%',
            width:            progress + '%',
            background:       'linear-gradient(to right, #00e5cc, #7eb8ff)',
            borderRadius:     '1px',
            transition:       'width 0.15s ease',
            boxShadow:        '0 0 8px rgba(0,229,204,0.6)',
          }}/>
        </div>
      </div>

      {/* Status */}
      <div style={{
        fontSize:      '10px',
        color:         'rgba(255,255,255,0.25)',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        animation:     'splashFadeUp 0.8s 0.6s both',
        height:        '16px',
      }}>
        {STATUS[statusIdx]}
      </div>

      {/* Footer */}
      <div style={{
        position:      'absolute',
        bottom:        '24px',
        fontSize:      '10px',
        color:         'rgba(255,255,255,0.12)',
        letterSpacing: '0.04em',
        textAlign:     'center',
        animation:     'splashFadeUp 0.8s 0.7s both',
      }}>
        Powered by NOAA GFS · Open-Meteo · Solar System Scope
      </div>

      <style>{`
        @keyframes splashLogoIn {
          from { opacity:0; transform:scale(0.8) translateY(20px); }
          to   { opacity:1; transform:scale(1) translateY(0); }
        }
        @keyframes splashFadeUp {
          from { opacity:0; transform:translateY(16px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </div>
  );
}
